// src/Dashboard/pages/user/DashboardUser/components/CompetitionsPanel.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaCalendarCheck,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaTrophy,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getAdherentsByUser } from "../../../../../services/adherentServices";
import { getEventsByAdherent } from "../../../../../services/eventServices";
import { toAbsoluteEventPhotoUrl } from "../../../../../services/eventPhotoServices";

import CompetitionDetailsDrawer from "./CompetitionDetailsDrawer";
import EmptyState from "./EmptyState";
import {
  DASHBOARD_ROUTES,
  formatDateLongFR,
  toAbsoluteUploadUrl,
} from "../utils/dashboardUserUtils";

export default function CompetitionsPanel({ userId, onCounts }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const prevCountRef = useRef(-1);

  const load = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const adherents = await getAdherentsByUser(userId);
      const adherentList = Array.isArray(adherents) ? adherents : [];
      const allRows = [];

      for (const adherent of adherentList) {
        const response = await getEventsByAdherent(adherent.id).catch(() => null);
        const events = Array.isArray(response?.Events) ? response.Events : [];

        events.forEach((event) => {
          allRows.push({
            ...event,
            adherentId: adherent.id,
            adherentNom: `${adherent.nom || ""} ${adherent.prenom || ""}`.trim(),
            adherentPhoto: toAbsoluteUploadUrl(
              adherent.photo_url,
              "/img/user_avatar.jpg"
            ),
            resultat: event?.AdherentEvent?.resultats,
            classement: event?.AdherentEvent?.classement,
            observations: event?.AdherentEvent?.observations,
            isValidate: event?.AdherentEvent?.is_validate,
            coachPresent: event?.AdherentEvent?.coach_present,
          });
        });
      }

      allRows.sort(
        (a, b) => new Date(a.start || 0) - new Date(b.start || 0)
      );

      setRows(allRows);
    } catch (error) {
      console.error("[CompetitionsPanel] load error:", error);
      toast.error("Impossible de charger les inscriptions aux évènements.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!onCounts) return;
    if (prevCountRef.current === rows.length) return;

    prevCountRef.current = rows.length;
    onCounts({ total: rows.length });
  }, [rows.length, onCounts]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-48 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <EmptyState
        icon={FaCalendarCheck}
        title="Aucune inscription à un évènement"
        description="Les évènements auxquels vos adhérents sont inscrits apparaîtront ici."
        action={
          <button
            type="button"
            onClick={() => navigate(DASHBOARD_ROUTES.calendar)}
            className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700"
          >
            Voir le calendrier
          </button>
        }
      />
    );
  }

  return (
    <>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => navigate(DASHBOARD_ROUTES.calendar)}
          className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700"
        >
          Voir le calendrier
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {rows.map((event) => {
          const hasResults =
            event.resultat || event.classement || event.observations;

          return (
            <article
              key={`${event.id}-${event.adherentId}`}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex flex-col sm:flex-row">
                <div className="relative h-48 bg-slate-100 sm:h-auto sm:w-44 dark:bg-slate-950">
                  <img
                    src={
                      toAbsoluteEventPhotoUrl(event.photo_url) ||
                      "/img/no-image.jpg"
                    }
                    alt={event.titre || "Évènement"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(img) => {
                      img.currentTarget.src = "/img/no-image.jpg";
                    }}
                  />

                  <span className="absolute left-3 top-3 rounded-full bg-black/65 px-2.5 py-1 text-xs font-bold text-white">
                    {formatDateLongFR(event.start)}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-start gap-3">
                    <img
                      src={event.adherentPhoto}
                      alt={event.adherentNom}
                      className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      loading="lazy"
                      onError={(img) => {
                        img.currentTarget.src = "/img/user_avatar.jpg";
                      }}
                    />

                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 font-bold text-slate-900 dark:text-white">
                        {event.titre || "Évènement"}
                      </h3>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {event.adherentNom}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                    <p className="flex items-center gap-2">
                      <FaMapMarkerAlt className="text-rose-500" />
                      {event.lieu_event || "Lieu à préciser"}
                    </p>
                    <p className="flex items-center gap-2">
                      <FaCalendarCheck className="text-violet-500" />
                      {event.isValidate ? "Inscription validée" : "Pré-inscription"}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedEvent(event)}
                      className={[
                        "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-white transition",
                        hasResults
                          ? "bg-emerald-600 hover:bg-emerald-700"
                          : "bg-sky-600 hover:bg-sky-700",
                      ].join(" ")}
                    >
                      {hasResults ? <FaTrophy /> : <FaInfoCircle />}
                      {hasResults ? "Info & résultats" : "Informations"}
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <CompetitionDetailsDrawer
        event={selectedEvent}
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </>
  );
}